/**
 * ONE definition of what a wetness reading MEANS.
 *
 * The dashboard's Trail Conditions page took the condition ramp — a
 * dry trail is green because a dry trail is open, and the engine has
 * never closed one for being dry. The field app did not: it kept the
 * six-colour palette both surfaces used to share (brown for parched,
 * green for a sweet spot, teal, blue, red), so the same reading was
 * amber on a phone and green on a desk. Reported as exactly that.
 *
 * What the two surfaces disagreed about was never the arithmetic. It
 * was the palette, and the palette is the half that legitimately
 * differs: the dashboard is built on `tiq-ui.css`'s chip ramp and the
 * field app on its own hexes, and a bar that borrowed the dashboard's
 * forest green would sit beside a field-app pill wearing a different
 * green for the same word.
 *
 * So this answers the ROLE and never a colour — the rule
 * `lib/conditionTone.js` already states, for the same reason: a
 * helper that names a colour is a colour nobody can retune. Each
 * surface maps `open` / `caution` / `closed` onto its own three,
 * which is what makes them agree about the reading while still
 * looking like themselves.
 *
 * THE BREAKPOINTS ARE THE ENGINE'S, not the label's. `CloseThresholds`
 * closes at its threshold and cautions at 65% of it. A network bar has
 * no single trail, so it scores against `DEFAULT_FALLBACK` — the same
 * number the engine falls back to when a trail's surface / drainage /
 * use lookup misses.
 *
 * `harness/wetnesstones_verify.php` reads both numbers out of the PHP
 * and fails if this drifts from them.
 */

export const WETNESS_CLOSE_PCT = 50;      // CloseThresholds::DEFAULT_FALLBACK
export const WETNESS_CAUTION_RATIO = 0.65; // CloseThresholds::CAUTION_RATIO_WETNESS

/**
 * What this reading means for the trail: 'open' | 'caution' | 'closed',
 * or null when there is no reading at all — which is not a fourth rung
 * on the ramp and must not be coloured like one.
 */
export function wetnessTone(pct) {
    if (pct == null) return null;
    if (pct >= WETNESS_CLOSE_PCT) return 'closed';
    if (pct >= WETNESS_CLOSE_PCT * WETNESS_CAUTION_RATIO) return 'caution';

    return 'open';
}

/**
 * How wet the ground is — a DIFFERENT question from what it means,
 * which is why six steps sit under three tones on the same bar.
 * Mirrors `TrailWetnessScore::label`, so the bar's own label stays in
 * sync when it shows the per-network value (the payload's
 * `wetness_label` is the centroid's).
 */
export function wetnessLabel(pct) {
    if (pct == null) return 'No data';
    if (pct < 15) return 'Bone Dry';
    if (pct < 30) return 'Drying';
    if (pct < 45) return 'Damp';
    if (pct < 60) return 'Wet';
    if (pct < 80) return 'Very Wet';

    return 'Saturated';
}
